import { ReactElement } from 'react';
import { Button, Dropdown } from '@jobcost-monorepo/ui';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyValue } from '@jobcost-monorepo/utils';
import {
  getMonthsKeyValues,
  getYearsKeyValues,
} from '@jobcost-monorepo/crud/utils/helpers/helperKeyValues';
import {
  IListOrderFetchParams,
  IResource,
  IResourceFetchParams,
  MN_RESOURCES,
} from '@jobcost-monorepo/crud';
import DropdownCrud from '@jobcost-monorepo/crud/ui/DropdownCrud';
import { OrdersSchedulesSchema } from './validationSchema';
import dayjs from '@jobcost-monorepo/dayjs-config';

interface ISearch {
  startMonth: number;
  endMonth: number;
  year: number;
  id?: number;
}

interface IProps {
  handleSearch: (
    startDate: string,
    endDate: string,
    resourceId?: number,
  ) => void;
  params?: IListOrderFetchParams;
}

const OrderScheduledHeader = ({
  handleSearch,
  params,
}: IProps): ReactElement => {
  const { t } = useTranslation(['orders', 'commons']);
  const months: KeyValue[] = getMonthsKeyValues();
  const years: KeyValue[] = getYearsKeyValues();
  const defaultValues: ISearch = {
    startMonth: dayjs.utc(params?.startDate).month(),
    endMonth: dayjs.utc(params?.endDate).month(),
    year: dayjs.utc(params?.startDate).year(),
    id: params?.resourceId,
  };

  const onSubmit: SubmitHandler<ISearch> = (data) => {
    const startDate = dayjs
      .utc(`${data.year}-${data.startMonth + 1}-01`)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = dayjs
      .utc(`${data.year}-${data.endMonth + 1}-01`)
      .endOf('month')
      .format('YYYY-MM-DD');
    const resourceId = data.id;

    handleSearch(startDate, endDate, resourceId);
  };

  const methods = useForm<ISearch>({
    mode: 'onSubmit',
    values: defaultValues,
    defaultValues: JSON.parse(JSON.stringify(defaultValues)),
    resolver: zodResolver(OrdersSchedulesSchema),
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full mb-2">
        <div className="w-full flex overflow-y-auto flex-wrap gap-2 items-center">
          <Dropdown
            name="year"
            errorName="year"
            label={t('filter.year')}
            items={years}
            searchable
            width="w-24"
          />
          <Dropdown
            name="startMonth"
            errorName="startMonth"
            label={t('filter.startMonth')}
            items={months}
            searchable
            width="w-32"
          />
          <Dropdown
            name="endMonth"
            errorName="endMonth"
            label={t('filter.endMonth')}
            items={months}
            searchable
            width="w-32"
          />
          <DropdownCrud<IResource, IResourceFetchParams>
            moduleName={MN_RESOURCES}
            linkedField={'id'}
            linkedFieldForDefaultValue={'firstName'}
            label={t('filter.resourceId')}
            width="w-56 md:w-32 xl:w-56"
          />
          <Button type="submit">{t('filter.update')}</Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default OrderScheduledHeader;
